import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { gbp } from "../../lib/apiClient";

/**
 * Section — card wrapper with title bar + optional action button on the right.
 *
 * Optional collapsibility (with state persisted to localStorage):
 *   <Section collapsible storageKey={`qb:${quoteId}:materials`} defaultOpen>…
 * If `storageKey` is provided, the open/closed state survives page reloads
 * AND is scoped per-quote (or "new" for unsaved quotes).
 */
export function Section({
  title,
  subtitle,
  action,
  children,
  collapsible = false,
  storageKey,
  defaultOpen = true,
}) {
  const [open, setOpen] = useState(() => {
    if (!collapsible || !storageKey || typeof window === "undefined") return true;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === "0") return false;
      if (raw === "1") return true;
    } catch { /* ignore */ }
    return defaultOpen;
  });

  // Persist on toggle
  useEffect(() => {
    if (!collapsible || !storageKey || typeof window === "undefined") return;
    try { window.localStorage.setItem(storageKey, open ? "1" : "0"); } catch { /* ignore */ }
  }, [open, collapsible, storageKey]);

  const showAction = action && (open || !collapsible);
  const HeaderTag = collapsible ? "button" : "div";
  const headerProps = collapsible
    ? {
        type: "button",
        onClick: () => setOpen((o) => !o),
        "aria-expanded": open,
        "data-testid": storageKey ? `section-toggle-${storageKey}` : undefined,
        className: "flex items-center gap-2 text-left min-w-0 flex-1 group",
      }
    : { className: "min-w-0 flex-1" };

  return (
    <div className="qb-card">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <HeaderTag {...headerProps}>
          {collapsible && (
            <ChevronDown
              size={14}
              className={`flex-shrink-0 text-slate-400 transition-transform group-hover:text-brand-blue ${open ? "" : "-rotate-90"}`}
              aria-hidden="true"
            />
          )}
          <div className="min-w-0">
            <div className="qb-section-title text-base">{title}</div>
            {subtitle && open && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
          </div>
        </HeaderTag>
        {showAction && action}
      </div>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
}

/* Grid — three-column responsive grid used for the Customer & Job form fields. */
export function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>;
}

/* Field — label + input wrapper that spans 1 or 3 columns. */
export function Field({ label, children, colSpan = 1, testid, required }) {
  return (
    <div className={`col-span-1 ${colSpan === 3 ? "sm:col-span-2 lg:col-span-3" : ""}`} data-testid={testid}>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 mb-1 block">
        {label}{required && <span className="text-red-600 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

/* SummaryGroup — labelled block in the Live Calculation sidebar. */
export function SummaryGroup({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

/* SummaryRow — single line in a SummaryGroup. */
export function SummaryRow({ k, v, strong, testid }) {
  return (
    <div
      className={`flex items-center justify-between text-sm ${strong ? "font-semibold border-t border-slate-300 pt-1 mt-1" : ""}`}
      data-testid={testid}
    >
      <span className="text-slate-700">{k}</span>
      <span className="font-mono">{v}</span>
    </div>
  );
}

/* SmallStat — compact stat tile (label left, money right). */
export function SmallStat({ label, v }) {
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2 flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
      <span className="font-mono font-semibold text-slate-900">{gbp(v)}</span>
    </div>
  );
}
